import { DIAG_TAP_COUNT, DIAG_TAP_WINDOW_MS } from '../config/kiosk'
import { deviceInfo } from './fully'

/**
 * Hidden bring-up panel.
 *
 * Tap the top-left 140x140px corner DIAG_TAP_COUNT times within
 * DIAG_TAP_WINDOW_MS to see what the device actually reports — WebView version
 * above all, since that is what decides which CSS/JS this app may use — and how
 * the slideshow is doing (last refresh, slide count, build).
 *
 * There is no on-screen affordance for this on purpose: visitors must not be
 * able to find it, and staff should not need a keyboard.
 */
export function installDiagnostics(stage: HTMLElement, appInfo: () => Promise<Record<string, string>>): void {
  const corner = document.createElement('div')
  corner.className = 'diag-corner'
  corner.setAttribute('aria-hidden', 'true')
  stage.appendChild(corner)

  let taps = 0
  let firstTapAt = 0

  corner.addEventListener('pointerdown', (event) => {
    // The slideshow underneath must not treat these taps as a swipe.
    event.stopPropagation()
    const now = Date.now()
    if (now - firstTapAt > DIAG_TAP_WINDOW_MS) {
      taps = 0
      firstTapAt = now
    }
    taps += 1
    if (taps >= DIAG_TAP_COUNT) {
      taps = 0
      void openPanel(stage, appInfo)
    }
  })
}

async function openPanel(stage: HTMLElement, appInfo: () => Promise<Record<string, string>>): Promise<void> {
  const existing = stage.querySelector('.diag-panel')
  if (existing) {
    existing.remove()
    return
  }

  let app: Record<string, string>
  try {
    app = await appInfo()
  } catch (error) {
    app = { error: String(error) }
  }
  const info: Record<string, unknown> = { ...app, ...deviceInfo() }

  const panel = document.createElement('div')
  panel.className = 'diag-panel'

  const rows = Object.keys(info)
    .map((key) => `<tr><th>${escapeHtml(key)}</th><td>${escapeHtml(String(info[key]))}</td></tr>`)
    .join('')

  panel.innerHTML = `
    <h2>디바이스 진단</h2>
    <table>${rows}</table>
    <button type="button" class="diag-close">닫기</button>
  `
  panel.querySelector('.diag-close')?.addEventListener('click', () => panel.remove())
  // Taps on the panel are not swipes on the slideshow underneath.
  panel.addEventListener('pointerdown', (event) => event.stopPropagation())
  stage.appendChild(panel)
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch,
  )
}
